import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';

/**
 * A generic loading link component for table cells
 * Shows a loading spinner when clicked and prevents multiple clicks
 * 
 * @param {Object} props
 * @param {Function} props.onClick - Async function to execute when clicked
 * @param {string} props.children - Text content to display (e.g., "view", "edit", "delete")
 * @param {string} [props.className] - CSS class name (defaults to "editLink")
 * @param {Object} [props.style] - Additional inline styles
 * @param {boolean} [props.disabled] - External disabled state
 * @param {number} [props.spinnerSize] - Size of the loading spinner (defaults to 16)
 * @param {string} [props.loadingText] - Text to show while loading (defaults to children)
 * @param {Object} [props.spinnerProps] - Additional props for CircularProgress
 */
const LoadingLink = ({
  onClick,
  children,
  className = 'editLink',
  style = {},
  disabled = false,
  spinnerSize = 16,
  loadingText,
  spinnerProps = {},
  ...otherProps
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (event) => {
    // Prevent default and stop propagation to avoid table row selection
    event?.preventDefault();
    event?.stopPropagation();

    // Don't execute if already loading or externally disabled
    if (isLoading || disabled || !onClick) return;

    setIsLoading(true);
    
    try {
      await onClick(event);
    } catch (error) {
      // Let the parent component handle the error
      console.error('LoadingLink error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = isLoading || disabled;
  const displayText = isLoading && loadingText ? loadingText : children;

  return (
    <div
      className={className}
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.7 : 1,
        userSelect: 'none',
        ...style
      }}
      {...otherProps}
    >
      {displayText}
      {isLoading && (
        <CircularProgress 
          size={spinnerSize} 
          {...spinnerProps}
        />
      )}
    </div>
  );
};

LoadingLink.propTypes = {
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  disabled: PropTypes.bool,
  spinnerSize: PropTypes.number,
  loadingText: PropTypes.string,
  spinnerProps: PropTypes.object,
};

LoadingLink.defaultProps = {
  className: 'editLink',
  style: {},
  disabled: false,
  spinnerSize: 16,
  loadingText: null,
  spinnerProps: {},
};

export default LoadingLink;
